import { css } from '@emotion/css'
import type { CSSObject } from '@emotion/react'
import { Pagination } from '@mantine/core'
import { IconCircleLeft } from 'components/icons/IconCircleLeft'
import { IconCircleRight } from 'components/icons/IconCircleRight'
import { TableLengthText } from 'components/TableLengthText/TableLengthText'
import { translate } from 'i18n/i18n'
import { isEqual } from 'lodash'
import type { CSSProperties, ReactNode } from 'react'
import { memo, useCallback, useEffect, useState } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'
import { FixedSizeList as List } from 'react-window'
import { colors } from 'settings/colors'
import { sortByKey } from '../../utils/sortByKey'
import { BodyRow } from './BodyRow/BodyRow'
import { ValuesBodyRow } from './BodyRow/ValuesBodyRow'
import { HeaderRow } from './HeaderRow/HeaderRow'
import { ValuesHeaderRow } from './HeaderRow/ValuesHeaderRow'
import type { TColumn, TColumnGroup, TColumnSorting, TDataElement } from './SummaryTable.types'

const genericSortingFunction = (a: TDataElement, b: TDataElement, dataIndex?: string) => {
  const aVal = dataIndex ? a[dataIndex] : a
  const bVal = dataIndex ? b[dataIndex] : b

  // Null/undefined always at the end (bottom)
  if (aVal === null && bVal === null) return 0

  if (aVal === null) return 1 // a is null, b is value => a goes after b

  if (bVal === null) return -1 // b is null, a is value => b goes after a

  // Numbers: sort numerically
  if (typeof aVal === 'number' && typeof bVal === 'number') {
    return aVal - bVal
  }

  // Strings (or fallback)
  return String(aVal).localeCompare(String(bVal), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

const SummaryTable = ({
  type = 'default',
  autoSortColumns,
  defaultSortedColumns,
  columnGroups = [],
  columns = [],
  data = [],
  noDataText = translate.phrases.banyanApp('No data found.'),
  onRow,
  pageSize,
  paginationResetKey,
  rowCSS,
  rowKey,
  showColumnTitles = true,
  tableTitle = '',
  thRowStyle = {},
  wrapperCSS = {},
  onPageChange,
}: {
  type: string
  autoSortColumns?: string[]
  defaultSortedColumns?: string[]
  columnGroups?: TColumnGroup[]
  columns: TColumn[]
  data: TDataElement[]
  noDataText?: ReactNode
  onRow?: (row: TDataElement, rowIndex: number) => { [key: string]: () => void }
  pageSize?: number
  paginationResetKey?: string
  rowCSS?: (row: TDataElement, rowIndex: number) => CSSProperties
  rowKey?: (row: TDataElement, rowIndex: number) => string
  showColumnTitles?: boolean
  tableTitle?: ReactNode
  thRowStyle?: CSSProperties
  wrapperCSS?: CSSObject
  onPageChange?: (page: number) => void
}) => {
  const [columnSorting, setColumnSorting] = useState<TColumnSorting>({
    order: null,
    column: '',
    sortingFunction: genericSortingFunction,
  })

  const ROW_VIRTUALIZE_THRESHOLD = 200
  const rowHeight = 38
  const defaultPagination = { min: 0, max: pageSize ?? data.length }
  const [paginationMinMax, setPaginationMinMax] = useState(defaultPagination)
  const [currentPage, setCurrentPage] = useState(1)
  const { sortingFunction, order: sortOrder, column: sortColumn } = columnSorting
  const usePagination = !!(pageSize && data.length > pageSize)

  const onColumnSorterClick = (col: TColumn) => {
    // if we're clicking on a new column, sort it ascending
    if (sortColumn !== col.key) {
      return setColumnSorting({
        ...columnSorting,
        column: col.key,
        order: 1,
        sortingFunction: col.sorter ?? ((a, b) => genericSortingFunction(a, b, col.dataIndex)),
      })
    }

    // If the user clicks on a column that is currently sorted in ascending order, we will sort it in descending order instead.
    if (sortColumn === col.key && sortOrder === 1) {
      return setColumnSorting({ ...columnSorting, order: -1 })
    }

    // if we're reclicking on the actively descendedly sorted column, remove sorting
    return setColumnSorting({
      order: null,
      column: '',
      sortingFunction: col.sorter ?? ((a, b) => genericSortingFunction(a, b, col.dataIndex)),
    })
  }

  let sortedData = data

  if (sortOrder) {
    sortedData = data.slice().sort((a, b) => sortingFunction(a, b) * sortOrder)
  }

  let paginatedData = sortedData.slice(paginationMinMax.min, paginationMinMax.max)

  useEffect(() => {
    setPaginationMinMax(defaultPagination)

    setCurrentPage(1)
  }, [paginationResetKey])

  useEffect(() => {
    if (type === 'values' && !columnSorting.order && columns.length > 0 && defaultSortedColumns?.length) {
      // Find the first column matching a dataIndex in the array
      const firstMatch = defaultSortedColumns
        .map((idx) => columns.find((col) => col.dataIndex === idx))
        .find(Boolean)

      if (firstMatch) {
        setColumnSorting({
          order: 1, // ascending; use -1 for descending
          column: firstMatch.key,
          sortingFunction: firstMatch.sorter ?? sortByKey(firstMatch.dataIndex),
        })

        return
      }
    }

    // fallback to previous behavior if not found
    if (type === 'values' && !columnSorting.order && columns.length > 0) {
      const firstCol = columns[0]

      setColumnSorting({
        order: 1, // ascending default
        column: firstCol.key,
        sortingFunction: firstCol.sorter ?? sortByKey(firstCol.dataIndex),
      })
    }
  }, [type, columns, defaultSortedColumns])

  const handlePaginationChange = useCallback(
    (pageNum: number): void => {
      if (usePagination) {
        if (pageNum <= 1) {
          setPaginationMinMax(defaultPagination)

          setCurrentPage(1)

          onPageChange?.(1)
        } else {
          setCurrentPage(pageNum)

          onPageChange?.(pageNum)

          setPaginationMinMax({ min: (pageNum - 1) * pageSize, max: pageNum * pageSize })
        }
      }
    },
    [setPaginationMinMax, setCurrentPage, onPageChange],
  )

  const showNoDataText = !sortedData || !sortedData.length

  const defaultCSS: CSSObject = {
    tableDiv: {
      paddingBottom: 12,
      marginBottom: -12,
      overflow: 'auto',
    },
    table: {
      borderCollapse: 'collapse',
      borderSpacing: 1,
      width: '100%',
      fontSize: 14,
    },
  }

  const valuesCSS: CSSObject = {
    tableDiv: {
      paddingBottom: 12,
      marginBottom: -12,
      overflowX: 'auto',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '40px 100%, 40px 100%, 14px 100%, 14px 100%',
      backgroundAttachment: 'local, local, scroll, scroll',
    },
    table: {
      'borderCollapse': 'separate',
      'borderSpacing': 1,
      'width': 'max-content',
      'minWidth': '100%',
      'fontSize': 14,
      'tableLayout': type === 'values' ? 'auto' : 'fixed',
      'borderTop': 'none', // Remove table border
      '& th': {
        'backgroundColor': 'white',
        'transition': 'background-color 0.2s',
        '&:hover': {
          backgroundColor: colors.grey50,
        },
      },
      '& td:first-of-type': {
        borderRight: `1px solid ${colors.grey200}`, // Add right border to first column
      },
    },
  }

  const VirtualizedRow = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const row = sortedData[index]

    return (
      <tr style={style} key={rowKey ? rowKey(row, index) : index}>
        {type === 'values' ? (
          <ValuesBodyRow columns={columns} row={row} rowIndex={index} onRow={onRow} rowCSS={rowCSS} />
        ) : (
          <BodyRow columns={columns} row={row} rowIndex={index} onRow={onRow} rowCSS={rowCSS} />
        )}
      </tr>
    )
  }

  const renderTableBody = () => {
    if (showNoDataText) {
      return (
        <tr>
          <td colSpan={columns.length}>
            <div css={{ visibility: 'hidden', paddingBottom: 18 }}>{noDataText}</div>
          </td>
        </tr>
      )
    }

    if (sortedData.length > ROW_VIRTUALIZE_THRESHOLD && type === 'values') {
      return (
        <tr>
          <td colSpan={columns.length} style={{ padding: 0, border: 'none' }}>
            <div style={{ width: '100%', height: Math.min(sortedData.length, 15) * rowHeight }}>
              <AutoSizer>
                {({ width, height }) => (
                  <List
                    height={height}
                    width={width}
                    itemCount={sortedData.length}
                    itemSize={rowHeight}
                    overscanCount={10}
                  >
                    {({ index, style }) => <VirtualizedRow index={index} style={style} />}
                  </List>
                )}
              </AutoSizer>
            </div>
          </td>
        </tr>
      )
    }

    // Standard map rendering for rows
    return paginatedData.map((row, i) =>
      type === 'values' ? (
        <ValuesBodyRow
          columns={columns}
          key={rowKey ? rowKey(row, i) : i}
          row={row}
          rowIndex={i}
          onRow={onRow}
          rowCSS={rowCSS}
        />
      ) : (
        <BodyRow
          columns={columns}
          key={rowKey ? rowKey(row, i) : i}
          row={row}
          rowIndex={i}
          onRow={onRow}
          rowCSS={rowCSS}
        />
      ),
    )
  }

  return (
    <div css={{ position: 'relative', width: '100%', ...wrapperCSS }}>
      {!showNoDataText ? null : (
        <div
          css={{
            textAlign: 'center',
            color: 'rgba(0, 0, 0, 0.45)',
            position: 'absolute',
            width: '100%',
            left: 0,
            right: 0,
            bottom: 12,
          }}
        >
          {noDataText}
        </div>
      )}
      <div css={type === 'values' ? valuesCSS.tableDiv : defaultCSS.tableDiv}>
        <table css={type === 'values' ? valuesCSS.table : defaultCSS.table}>
          {tableTitle ? (
            <thead css={{ borderBottom: `1px solid ${colors.midnight}` }}>
              <tr>
                <th
                  css={{
                    'textAlign': 'left',
                    'padding': 8,
                    '&:first-of-type': {
                      fontWeight: 600,
                      paddingLeft: '14px !important',
                    },
                  }}
                  colSpan={columns.length}
                >
                  {tableTitle}
                </th>
              </tr>
            </thead>
          ) : null}
          {!!showColumnTitles && (
            <thead css={{ borderBottom: `1px solid ${colors.midnight}` }}>
              {!!columnGroups.length && (
                <tr>
                  {columnGroups.map((group: TColumnGroup) => {
                    return (
                      <th
                        css={{
                          'textAlign': 'left',
                          'padding': '8px 8px 0',
                          '&:first-of-type': {
                            fontWeight: 600,
                            paddingLeft: '14px !important',
                          },
                        }}
                        key={group.key}
                        colSpan={group.span}
                      >
                        {group.title}
                      </th>
                    )
                  })}
                </tr>
              )}
              <tr>
                {columns.map((col) =>
                  type === 'values' ? (
                    <ValuesHeaderRow
                      autoSortColumns={autoSortColumns}
                      col={col}
                      columnSorting={columnSorting}
                      enoughDataToShowSorting={sortedData && sortedData.length >= 2}
                      key={col.key}
                      onColumnSorterClick={onColumnSorterClick}
                      thRowStyle={thRowStyle}
                    />
                  ) : (
                    <HeaderRow
                      autoSortColumns={autoSortColumns}
                      col={col}
                      columnSorting={columnSorting}
                      enoughDataToShowSorting={sortedData && sortedData.length >= 2}
                      key={col.key}
                      onColumnSorterClick={onColumnSorterClick}
                      thRowStyle={thRowStyle}
                      type={type}
                    />
                  ),
                )}
              </tr>
            </thead>
          )}
          <tbody>{renderTableBody()}</tbody>
        </table>
        {usePagination && type === 'default' && (
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              margin: '20px auto',
            }}
          >
            <TableLengthText
              filteredRows={sortedData.length}
              totalRows={data.length}
              activePage={currentPage}
              pageSize={pageSize}
            />
            <Pagination
              unstyled
              classNames={{
                control: css({
                  'border': 'none',
                  '&[data-active]': {
                    fontWeight: 'bold',
                    backgroundColor: 'transparent',
                    color: colors.midnight,
                  },
                  '&:first-of-type, &:last-of-type': {
                    fontSize: 30,
                  },
                  '&:not(:first-of-type):not(:last-of-type)': {
                    marginBottom: '0.3rem',
                  },
                }),
              }}
              css={{ margin: 'auto', textAlign: 'center' }}
              onChange={handlePaginationChange}
              total={Math.ceil(sortedData.length / pageSize)}
              value={currentPage}
              previousIcon={IconCircleLeft}
              nextIcon={IconCircleRight}
            />
          </div>
        )}
      </div>
    </div>
  )
}

const MemoizedSummaryTable = memo(SummaryTable, isEqual) as typeof SummaryTable

export { MemoizedSummaryTable as SummaryTable }
